import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  AlertCircle,
  Clock,
  FileText,
  Calendar,
  Phone,
  Mail,
  MessageSquare,
  Eye,
  CheckCircle,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useToast } from '../../hooks/use-toast';
import { leadService } from '../../services/leadService';

const CRMAlertas = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [alerts, setAlerts] = useState({
    followup_overdue: [],
    inactive_leads: [],
    expiring_proposals: [],
    total_alerts: 0
  });
  const [loading, setLoading] = useState(true);

  // Carregar alertas
  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = async () => {
    setLoading(true);
    try {
      const data = await leadService.getAlerts();
      setAlerts(data);
    } catch (error) {
      console.error('Erro ao carregar alertas:', error);
      toast({
        title: 'Erro ao carregar alertas',
        description: 'Não foi possível carregar os alertas.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Marcar alerta como resolvido (atualizar follow-up date)
  const handleResolveFollowup = async (leadId) => {
    try {
      const nextWeek = new Date();
      nextWeek.setDate(nextWeek.getDate() + 7);
      
      await leadService.update(leadId, {
        next_followup_date: nextWeek.toISOString()
      });

      toast({
        title: 'Follow-up atualizado',
        description: 'Próximo follow-up agendado para daqui a 7 dias.',
      });

      loadAlerts();
    } catch (error) {
      console.error('Erro ao atualizar:', error);
      toast({
        title: 'Erro ao atualizar',
        description: 'Não foi possível atualizar o follow-up.',
        variant: 'destructive',
      });
    }
  };

  const handleViewLead = (leadId) => {
    sessionStorage.setItem('openLeadDetail', leadId);
    navigate('/portal/leads');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Carregando alertas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/portal/leads')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Central de Alertas</h1>
            <p className="text-gray-600 mt-1">
              Leads que precisam da sua atenção
            </p>
          </div>
        </div>

        <Button onClick={loadAlerts} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Resumo de Alertas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-orange-800 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Follow-ups Vencidos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">
              {alerts.followup_overdue?.length || 0}
            </div>
            <p className="text-xs text-orange-700 mt-1">
              com data de retorno vencida
            </p>
          </CardContent>
        </Card>

        <Card className="border-red-200 bg-red-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-red-800 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Leads Inativos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600">
              {alerts.inactive_leads?.length || 0}
            </div>
            <p className="text-xs text-red-700 mt-1">
              sem contato há mais de 7 dias
            </p>
          </CardContent>
        </Card>

        <Card className="border-purple-200 bg-purple-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-purple-800 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Propostas Expirando
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">
              {alerts.expiring_proposals?.length || 0}
            </div>
            <p className="text-xs text-purple-700 mt-1">
              expirando em até 3 dias
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Abas de Alertas */}
      <Tabs defaultValue="followups" className="space-y-4">
        <TabsList>
          <TabsTrigger value="followups">
            Follow-ups Vencidos ({alerts.followup_overdue?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="inativos">
            Leads Inativos ({alerts.inactive_leads?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="propostas">
            Propostas Expirando ({alerts.expiring_proposals?.length || 0})
          </TabsTrigger>
        </TabsList>

        {/* ABA: Follow-ups Vencidos */}
        <TabsContent value="followups">
          <Card>
            <CardHeader>
              <CardTitle>Follow-ups com Data Vencida</CardTitle>
              <CardDescription>
                Leads com retorno agendado que já passou da data
              </CardDescription>
            </CardHeader>
            <CardContent>
              {alerts.followup_overdue?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Data do Follow-up</TableHead>
                      <TableHead>Mensagem</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {alerts.followup_overdue.map((lead) => (
                      <TableRow key={lead._id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{lead.company_name}</p>
                            <p className="text-sm text-gray-500">{lead.contact_person}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={leadService.getStatusColor(lead.status)}>
                            {leadService.translateStatus(lead.status)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-red-600">
                            <Calendar className="h-4 w-4" />
                            {lead.next_followup_date ? new Date(lead.next_followup_date).toLocaleDateString('pt-BR') : 'N/A'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="destructive" className="text-xs">
                            {lead.alert_message}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewLead(lead._id)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Ver
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleResolveFollowup(lead._id)}
                              className="text-green-600 hover:text-green-700"
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Reagendar
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhum follow-up vencido</p>
                  <p className="text-sm mt-1">Todos os retornos estão em dia!</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ABA: Leads Inativos */}
        <TabsContent value="inativos">
          <Card>
            <CardHeader>
              <CardTitle>Leads sem Contato Recente</CardTitle>
              <CardDescription>
                Leads sem interação registrada há mais de 7 dias
              </CardDescription>
            </CardHeader>
            <CardContent>
              {alerts.inactive_leads?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Última Interação</TableHead>
                      <TableHead>Mensagem</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {alerts.inactive_leads.map((lead) => (
                      <TableRow key={lead._id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{lead.company_name}</p>
                            <p className="text-sm text-gray-500">{lead.contact_person}</p>
                            <div className="flex gap-2 mt-1">
                              <a
                                href={`tel:${lead.phone}`}
                                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                              >
                                <Phone className="h-3 w-3" />
                                {lead.phone}
                              </a>
                              <a
                                href={`mailto:${lead.email}`}
                                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                              >
                                <Mail className="h-3 w-3" />
                                Email
                              </a>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={leadService.getStatusColor(lead.status)}>
                            {leadService.translateStatus(lead.status)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {lead.last_interaction_date ? (
                            <div>
                              <p className="text-sm">
                                {new Date(lead.last_interaction_date).toLocaleDateString('pt-BR')}
                              </p>
                              <p className="text-xs text-gray-500">
                                há {leadService.daysSinceLastInteraction(lead.last_interaction_date)} dia(s)
                              </p>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">Sem interações</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-800">
                            {lead.alert_message}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewLead(lead._id)}
                            >
                              <MessageSquare className="h-4 w-4 mr-1" />
                              Registrar Contato
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 opacity-50 text-green-500" />
                  <p>Nenhum lead inativo</p>
                  <p className="text-sm mt-1">Todos os leads foram contatados recentemente!</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ABA: Propostas Expirando */}
        <TabsContent value="propostas">
          <Card>
            <CardHeader>
              <CardTitle>Propostas Próximas do Vencimento</CardTitle>
              <CardDescription>
                Propostas que expiram nos próximos 3 dias
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Mensagem Informativa sobre Status de Propostas */}
              <Alert className="mb-4 bg-purple-50 border-purple-200">
                <AlertCircle className="h-4 w-4 text-purple-600" />
                <AlertDescription className="text-sm text-purple-800">
                  <strong>Importante:</strong> Apenas propostas com status <Badge variant="outline" className="mx-1 text-purple-700 border-purple-300">Enviada</Badge> ou <Badge variant="outline" className="mx-1 text-purple-700 border-purple-300">Em Análise</Badge> aparecem nesta lista. 
                  Propostas em rascunho não são exibidas nos alertas.
                </AlertDescription>
              </Alert>

              {alerts.expiring_proposals?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Proposta</TableHead>
                      <TableHead>Vencimento</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {alerts.expiring_proposals.map((lead) => (
                      <TableRow key={lead._id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{lead.company_name}</p>
                            <p className="text-sm text-gray-500">{lead.contact_person}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={leadService.getStatusColor(lead.status)}>
                            {leadService.translateStatus(lead.status)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{lead.proposal_title || 'Proposta'}</p>
                            {lead.expiration_date && (
                              <p className="text-xs text-gray-500">
                                Expira em {Math.ceil((new Date(lead.expiration_date) - new Date()) / (1000 * 60 * 60 * 24))} dia(s)
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-purple-600">
                            <Calendar className="h-4 w-4" />
                            {lead.expiration_date ? new Date(lead.expiration_date).toLocaleDateString('pt-BR') : 'N/A'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewLead(lead._id)}
                            >
                              <FileText className="h-4 w-4 mr-1" />
                              Ver Proposta
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhuma proposta expirando</p>
                  <p className="text-sm mt-1">Todas as propostas estão dentro do prazo!</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dicas */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <AlertCircle className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">Dicas para gerenciar alertas</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• <strong>Follow-ups vencidos:</strong> Use "Reagendar" para criar um novo lembrete automaticamente</li>
                <li>• <strong>Leads inativos:</strong> Registre um contato para tirar o lead da lista de alertas</li>
                <li>• <strong>Propostas expirando:</strong> Entre em contato com o cliente para confirmar interesse</li>
                <li>• Mantenha esta página com zero alertas para maximizar suas conversões!</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CRMAlertas;
